import { ApolloError, useMutation, useQuery } from '@apollo/client'
import {
    ShowGlobalSnackbarMutation,
    ShowGlobalSnackbarMutationProps,
    ShowGlobalSnackbarRequest,
} from '@deltek/specification-client/core/Configurations/GlobalSnackbar'
import { CurrentUserInfoQuery } from '@deltek/specification-client/core/GraphQL/Queries/currentUserInfo.queries'
import { CurrentUserInfoResponse } from '@deltek/specification-client/core/Models/currentUserInfo'
import {
    FirmId,
    FirmResponse,
} from '@deltek/specification-client/core/Models/firm'
import { nameValuePropType } from '@deltek/specification-client/core/Models/nameValuePropType'
import GridContainer from '@deltek/specification-client/core/Surfaces/GridContainer'
import GridItem from '@deltek/specification-client/core/Surfaces/GridItem'
import { pxToRem } from '@deltek/specification-client/core/Utilities/unitUtils'
import {
    FormControl,
    FormControlLabel,
    Popper,
    PopperProps,
    Radio,
    RadioGroup,
    TextField,
} from '@material-ui/core'
import Button from '@material-ui/core/Button'
import Dialog from '@material-ui/core/Dialog'
import DialogActions from '@material-ui/core/DialogActions'
import DialogContent from '@material-ui/core/DialogContent'
import DialogTitle from '@material-ui/core/DialogTitle'
import { Theme, makeStyles } from '@material-ui/core/styles'
import Autocomplete from '@material-ui/lab/Autocomplete'
import { ProjectGroupCountItem } from 'Models/project'
import { FirmProjectParametersQuery } from 'api/graphQL/queries/firm.queries'
import clsx from 'clsx'
import {
    RemoveProjectGroupAndMoveChildrenInput,
    RemoveProjectGroupInput,
} from 'models/projectGroupInput'
import { sortOptionsAlphabetically } from 'pages/ProjectsPage/components/NewProject'
import React, { ChangeEvent } from 'react'
import {
    RemoveProjectGroupAndMoveChildrenMutation,
    RemoveProjectGroupWithProjectsMutation,
} from '../../../../api/graphQL/mutations/projectGroup.mutation'
const useStyles = makeStyles((theme: Theme) => ({
    dialogTitle: {
        '& .MuiTypography-h6': {
            color: '#304959',
            fontSize: pxToRem(34),
        },
        '&.MuiDialogTitle-root': {
            paddingTop: theme.spacing(1.875),
            paddingRight: theme.spacing(0),
            paddingLeft: theme.spacing(3.125),
            paddingBottom: theme.spacing(0),
        },
    },
    dialogCont: {
        color: theme.palette.common.black,
        width: pxToRem(630),
        fontSize: pxToRem(18),
    },
    actionButton: {
        color: '#A78966',
        fontWeight: 400,
        fontSize: pxToRem(18),
        lineHeight: pxToRem(40),
        letterSpacing: pxToRem(0.25),
        '&.MuiButton-text:last-child': {
            marginRight: theme.spacing(3),
        },
    },
    dialogAction: {
        '&.MuiDialogActions-root': {
            paddingLeft: theme.spacing(0),
            paddingRight: theme.spacing(0),
        },
    },
    projectGroupDialogContainer: {
        '&.projectGroupDialogPosition': {
            zIndex: '1305 !important',
        },
    },
    popperStyle: {
        '&.popperPosition': {
            zIndex: '1306 !important',
        },
    },
    formLabelRadioContainer: {
        marginLeft: theme.spacing(0),
        '& span.MuiTypography-body1': {
            fontSize: pxToRem(13),
            color: '#5E5F61',
            fontWeight: 'normal',
        },
    },
    removeOptionRadioGroup: {},
    gridContainerRadioButton: {
        marginTop: theme.spacing(3.375),
    },
    projectGroupRadioButton: {
        color: theme.palette.common.black,
        border: pxToRem(1) + ' solid #C9C9C9',
        height: pxToRem(15),
        width: pxToRem(15),
        marginRight: theme.spacing(1),
        '&$checked': {
            color: theme.palette.common.black,
        },
        '& span': {
            '& div': {
                '& svg.MuiSvgIcon-root:first-child': {
                    color: 'transparent',
                },
            },
        },
    },
    checked: {},
}))

export interface RemoveProjectGroupDialogProps {
    /**
     * project group count
     */
    projectGroupCount: Partial<ProjectGroupCountItem>[] | undefined

    /**
     * Defines state of confirmation dialog.
     */
    openDialog: boolean

    /**
     * For closing confirmation dialog
     */
    handleDialogClose: VoidFunction

    /**
     * groupName selected
     */
    groupName: string

    /**
     * Refetch project table data on save
     */
    refetch: VoidFunction

    /**
     * Refetch recently viewed on save
     */
    refetchRecent: React.Dispatch<React.SetStateAction<boolean>>
}

const emptyProjectGroup = {
    name: '',
    value: '',
}

const removeProjectGroupOptions = [
    {
        label: 'Move All Projects To Other Group: ',
        value: 'moveProjects',
    },
    {
        label: 'Remove All Projects Under Group',
        value: 'removeProjects',
    },
]

const RemoveProjectGroupDialog: React.FC<RemoveProjectGroupDialogProps> = ({
    openDialog,
    handleDialogClose,
    refetch,
    groupName,
    projectGroupCount,
    refetchRecent,
}) => {
    const classes = useStyles()
    const [groupId, setGroupId] = React.useState<string>('')
    const [selectedOption, setSelectedOption] = React.useState<string>(
        'moveProjects'
    )
    const [projectGroupsDialog, setProjectGroupsDialog] = React.useState<
        Record<nameValuePropType, string>[]
    >([])
    const [selectedProjectGroup, setSelectedProjectGroup] = React.useState<
        Record<nameValuePropType, string>
    >()
    const [textfieldref, setTextFieldRef] = React.useState<
        React.RefObject<HTMLElement> | null | HTMLElement
    >(null)
    const [
        currentProjectGroupCount,
        setCurrentProjectGroupCount,
    ] = React.useState<number>(1)
    const [
        selectedMoveProjectGroup,
        setSelectedMoveProjectGroup,
    ] = React.useState<Record<nameValuePropType, string>>()

    const { data: currentUserInfoResponse } = useQuery<CurrentUserInfoResponse>(
        CurrentUserInfoQuery
    )
    const firmId =
        currentUserInfoResponse?.currentUserInfo?.account?.accountId ?? ''

    const [showGlobalSnackbar] = useMutation<
        ShowGlobalSnackbarRequest,
        ShowGlobalSnackbarMutationProps
    >(ShowGlobalSnackbarMutation)

    const popper = (props: PopperProps) => (
        <Popper
            {...props}
            placement="bottom"
            className={clsx(classes.popperStyle, 'popperPosition')}
        />
    )

    useQuery<FirmResponse, FirmId>(FirmProjectParametersQuery, {
        variables: {
            firmId: firmId ?? '',
        },
        onCompleted: (data) => {
            const projectGroups = (data.firm.projectGroups || []).map(
                (projectGroup) => {
                    return {
                        value: projectGroup.id!,
                        name: projectGroup.name!,
                    } as Record<nameValuePropType, string>
                }
            )
            var newProjectGroups = sortOptionsAlphabetically(projectGroups)
            setProjectGroupsDialog(newProjectGroups)
        },
    })

    const handleRemoveProjectGroupSave = () => {
        if (selectedOption === 'moveProjects') {
            const obj: RemoveProjectGroupAndMoveChildrenInput = {
                id: selectedProjectGroup?.value ?? '',
                projectGroupIdToMove: selectedMoveProjectGroup?.value ?? '',
            }
            removeProjectGroupAndMoveChildren({
                variables: {
                    removeProjectGroupInput: obj,
                },
            })
        } else {
            const obj: RemoveProjectGroupInput = {
                id: selectedProjectGroup?.value ?? '',
            }
            removeProjectGroupWithProjects({
                variables: {
                    removeProjectGroupInput: obj,
                },
            })
        }
    }

    const [removeProjectGroupWithProjects] = useMutation<{
        removeProjectGroupInput: RemoveProjectGroupInput
    }>(RemoveProjectGroupWithProjectsMutation, {
        onCompleted: () => {
            showGlobalSnackbar({
                variables: {
                    message: `Project group removed successfully.`,
                    variant: 'success',
                    id: `ProjectHomePage_ProjectGroupRemoveWithProjectsSuccess`,
                },
            })
            handleDialogClose()
            /*
             *   Added timeout to compensate for delay in updating of project groups on the API side
             */
            setTimeout(() => {
                refetchRecent(true)
                refetch()
            }, 1000)
        },
        onError: (error: ApolloError) => {
            showGlobalSnackbar({
                variables: {
                    message: error.message,
                    variant: 'error',
                    id: 'ProjectHomePage_ProjectGroupRemoveWithProjectsError',
                },
            })
        },
    })

    const [removeProjectGroupAndMoveChildren] = useMutation<{
        removeProjectGroupAndMoveChildrenInput: RemoveProjectGroupAndMoveChildrenInput
    }>(RemoveProjectGroupAndMoveChildrenMutation, {
        onCompleted: () => {
            showGlobalSnackbar({
                variables: {
                    message: `Project group removed and children moved successfully.`,
                    variant: 'success',
                    id: `ProjectHomePage_ProjectGroupRemoveAndMoveChildrenSuccess`,
                },
            })
            handleDialogClose()
            /*
             *   Added timeout to compensate for delay in updating of project groups on the API side
             */
            setTimeout(() => {
                refetchRecent(true)
                refetch()
            }, 1000)
        },
        onError: (error: ApolloError) => {
            showGlobalSnackbar({
                variables: {
                    message: error.message,
                    variant: 'error',
                    id:
                        'ProjectHomePage_ProjectGroupRemoveAndMoveChildrenError',
                },
            })
        },
    })

    const getId = React.useCallback(
        (groupName: string) => {
            projectGroupsDialog.forEach((el) => {
                el.name === groupName && setGroupId(el.value)
            })
        },
        [projectGroupsDialog]
    )

    const handleProjectGroupChange = (
        event: ChangeEvent<{}>,
        values: Record<nameValuePropType, string> | null
    ) => {
        if (values) {
            let count
            setSelectedProjectGroup(values)
            setGroupId(values.value)
            count = getProjectGroupCount(values.value) as number
            if (count === 0) {
                setSelectedOption('removeProjects')
                setSelectedMoveProjectGroup(emptyProjectGroup)
            } else if (currentProjectGroupCount === 0 && count > 0) {
                setSelectedOption('moveProjects')
            }
            setCurrentProjectGroupCount(count)
        } else {
            setSelectedProjectGroup({ name: '', value: '' })
        }
    }

    const handleMoveProjectGroupChange = (
        event: ChangeEvent<{}>,
        values: Record<nameValuePropType, string> | null
    ) => {
        values
            ? setSelectedMoveProjectGroup(values)
            : setSelectedMoveProjectGroup({ name: '', value: '' })
    }

    const getProjectToRemove = React.useCallback(
        (groupId: string) => {
            const projectFound =
                projectGroupsDialog.find(
                    (group: Record<nameValuePropType, string>) =>
                        group.value === groupId
                ) || null

            projectFound && setSelectedProjectGroup(projectFound)
        },
        [projectGroupsDialog]
    )

    const getProjectGroupCount = (projectGroupId: string) => {
        if (projectGroupCount && projectGroupId.length > 0) {
            let count = projectGroupCount.find(
                (el) => el.groupId === projectGroupId
            )?.totalCount
            if (count) {
                return count
            } else {
                return 0
            }
        } else {
            return ''
        }
    }

    React.useEffect(() => {
        if (groupName && projectGroupsDialog) {
            setSelectedProjectGroup({
                name: groupName,
                value: '',
            })
            getId(groupName)
        }
    }, [groupName, projectGroupsDialog, getId])

    React.useEffect(() => {
        if (groupId && projectGroupsDialog) {
            getProjectToRemove(groupId)
        }
    }, [groupId, projectGroupsDialog, getProjectToRemove])

    React.useEffect(() => {
        if (textfieldref) {
            setTimeout(() => {
                ;(textfieldref as HTMLElement).focus()
            }, 0)
        }
    }, [textfieldref])

    let isSaveDisabled = selectedProjectGroup?.name
        ? selectedOption === 'moveProjects'
            ? selectedMoveProjectGroup?.name
                ? false
                : true
            : false
        : true

    return (
        <Dialog
            className={clsx(
                classes.projectGroupDialogContainer,
                'projectGroupDialogPosition'
            )}
            open={openDialog}
            aria-labelledby="remove-project-group-properties"
        >
            <DialogTitle className={classes.dialogTitle}>
                Remove Project Group
            </DialogTitle>
            <DialogContent className={classes.dialogCont}>
                <FormControl fullWidth variant="outlined">
                    <GridContainer direction="column">
                        <GridItem xs={12}>
                            <Autocomplete
                                PopperComponent={popper}
                                size="small"
                                disabled={false}
                                getOptionLabel={(
                                    option: Record<nameValuePropType, string>
                                ) =>
                                    option.name.length > 0
                                        ? option.name +
                                          ` (${getProjectGroupCount(
                                              option.value
                                          )})`
                                        : ''
                                }
                                getOptionSelected={(option, item) =>
                                    option.value === item.value
                                }
                                options={
                                    selectedMoveProjectGroup
                                        ? projectGroupsDialog.filter(
                                              (pGroup) =>
                                                  pGroup.name !==
                                                  selectedMoveProjectGroup.name
                                          )
                                        : projectGroupsDialog
                                }
                                onChange={handleProjectGroupChange}
                                value={selectedProjectGroup}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        data-testid="projectGroupNameToRemoveField"
                                        inputRef={setTextFieldRef}
                                        label={'Project Group to be Removed: '}
                                        disabled={false}
                                        name="projectGroupToRemove"
                                        size="small"
                                        fullWidth
                                        InputLabelProps={{
                                            shrink: true,
                                        }}
                                        placeholder={'Select Project Group'}
                                    />
                                )}
                            />
                        </GridItem>
                    </GridContainer>
                    {currentProjectGroupCount > 0 && (
                        <>
                            <GridContainer
                                direction="column"
                                className={classes.gridContainerRadioButton}
                            >
                                <RadioGroup
                                    data-testid="projectGroupRemovalType"
                                    row
                                    aria-label="projectGroupRemovalType"
                                    className={classes.removeOptionRadioGroup}
                                    value={selectedOption}
                                >
                                    {removeProjectGroupOptions.map((el, i) => {
                                        return (
                                            <GridItem xs={6} key={i}>
                                                <FormControlLabel
                                                    className={
                                                        classes.formLabelRadioContainer
                                                    }
                                                    value={el.value}
                                                    control={
                                                        <Radio
                                                            size="small"
                                                            disableRipple
                                                            classes={{
                                                                root:
                                                                    classes.projectGroupRadioButton,
                                                                checked:
                                                                    classes.checked,
                                                            }}
                                                        />
                                                    }
                                                    label={el.label}
                                                    onClick={() =>
                                                        setSelectedOption(
                                                            el.value
                                                        )
                                                    }
                                                />
                                            </GridItem>
                                        )
                                    })}
                                </RadioGroup>
                            </GridContainer>
                            <GridContainer direction="column">
                                <GridItem xs={6}>
                                    <Autocomplete
                                        PopperComponent={popper}
                                        size="small"
                                        disabled={
                                            selectedOption === 'removeProjects'
                                        }
                                        getOptionLabel={(
                                            option: Record<
                                                nameValuePropType,
                                                string
                                            >
                                        ) =>
                                            option.name.length > 0
                                                ? option.name +
                                                  ` (${getProjectGroupCount(
                                                      option.value
                                                  )})`
                                                : ''
                                        }
                                        getOptionSelected={(option, item) =>
                                            option.value === item.value
                                        }
                                        options={
                                            selectedProjectGroup
                                                ? projectGroupsDialog.filter(
                                                      (pGroup) =>
                                                          pGroup.name !==
                                                          selectedProjectGroup.name
                                                  )
                                                : projectGroupsDialog
                                        }
                                        onChange={handleMoveProjectGroupChange}
                                        value={selectedMoveProjectGroup}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                data-testid="projectGroupNameToMoveProjectsField"
                                                disabled={
                                                    selectedOption ===
                                                    'removeProjects'
                                                }
                                                name="projectGroupToMoveProjects"
                                                size="small"
                                                fullWidth
                                                InputLabelProps={{
                                                    shrink: true,
                                                }}
                                                placeholder={
                                                    'Select Project Group'
                                                }
                                            />
                                        )}
                                    />
                                </GridItem>
                            </GridContainer>
                        </>
                    )}
                </FormControl>
            </DialogContent>
            <DialogActions className={classes.dialogAction}>
                <Button
                    className={classes.actionButton}
                    onClick={handleDialogClose}
                >
                    Cancel
                </Button>
                <Button
                    data-testid="saveRemoveProjectGroupButton"
                    disabled={isSaveDisabled}
                    onClick={handleRemoveProjectGroupSave}
                    className={classes.actionButton}
                >
                    Save
                </Button>
            </DialogActions>
        </Dialog>
    )
}

export default RemoveProjectGroupDialog
